/**
 * The HUD of decision 0013, fixed bars at the top and bottom (A, Daniel's choice of 2026-10-17
 * over the map sheet C), laid out by the places rule of decision 0015 (Daniel, 2026-10-19): the
 * top bar chooses which world shows, Today, Endless or the clearing, and the bottom bar holds what
 * belongs to the world shown. A phone layout and a desktop layout; CSS shows the one that fits.
 * Controls carry `data-action`, texts `data-bind`; the Ui fills and wires them.
 */
import type { StringKey } from "../i18n.ts";
import { esc, icon } from "./dom.ts";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

const compassButton = (t: T, size = 32) =>
  `<button type="button" class="cell icon-cell" data-action="north" aria-label="${esc(t("hud.north"))}">${icon("compass", size, "compass")}</button>`;

const cabinButton = (t: T) =>
  `<button type="button" class="cell icon-cell" data-action="cabin" aria-label="${esc(t("button.home"))}">${icon("cabin")}</button>`;

const bannerButton = (t: T, size: number, cls: string) =>
  `<button type="button" class="${cls}" data-action="banner" aria-pressed="false" aria-label="${esc(t("a11y.bannerMode"))}">${icon("banner", size)}</button>`;

const desktopTools = (t: T) => `
  <button type="button" class="cell tool" data-action="turnLeft" aria-label="${esc(t("hud.turnLeft"))}">${icon("turnLeft", 22)}<span class="key">${esc(t("hud.turnLeftKey"))}</span></button>
  <button type="button" class="cell tool" data-action="turnRight" aria-label="${esc(t("hud.turnRight"))}">${icon("turnRight", 22)}<span class="key">${esc(t("hud.turnRightKey"))}</span></button>
  <button type="button" class="cell icon-cell" data-action="zoomOut" aria-label="${esc(t("button.zoomOut"))}">${icon("zoomOut", 22)}</button>
  <button type="button" class="cell icon-cell" data-action="zoomIn" aria-label="${esc(t("button.zoomIn"))}">${icon("zoomIn", 22)}</button>`;

const getAppButton = (t: T) =>
  `<button type="button" class="cell labelled" data-action="getApp">${esc(t("menu.getApp"))}</button>`;

/** A place in the top bar: Today, Endless or the clearing, with its kicker. */
const placeTab = (action: string, cls: string, label: string, kicker: string) =>
  `<button type="button" class="${cls}" data-action="${action}" aria-pressed="false"><span class="tab">${esc(label)}</span><span class="kicker" data-bind="${kicker}"></span></button>`;

const places = (t: T, cls: string) => `
  ${placeTab("daily", cls, t("hud.today"), "dailyShort")}
  ${placeTab("endless", cls, t("button.endless"), "endlessKicker")}
  ${placeTab("clearing", `${cls} clearing-cell`, t("hud.clearing"), "found")}`;

/** The expedition's distance and its line, where the place's own things stand. */
const distanceCell = (t: T, kicker: string) => `
  <div class="cell stack status run-only" role="status" aria-label="${esc(t("a11y.distance"))}">
    <div class="hud-distance" data-bind="distance"></div>
    <div class="${kicker}" data-bind="mode"></div>
  </div>`;

/** Arranging and the photo mode, the clearing's own two ways of being looked at. */
const clearingModes = (t: T, cls: string) => `
  <button type="button" class="${cls} clearing-only" data-action="arrange" aria-pressed="true"><span class="tab">${esc(t("clearing.arrange"))}</span></button>
  <button type="button" class="${cls} clearing-only" data-action="photo" aria-pressed="false"><span class="tab">${esc(t("clearing.photo"))}</span></button>`;

const floatingCompass = (t: T) =>
  `<button type="button" class="float-compass only-phone run-only" data-action="north" aria-label="${esc(t("hud.north"))}">${icon("compass", 32, "compass")}</button>`;

/**
 * The row of the player's clearings under the top bar while the clearing shows, as Daniel's sketch
 * of 2026-10-22 draws it: a cell for each clearing, which the Ui fills, the chosen one in ink, and
 * the plus after them, which stays at the row's end when they run past it. A held clearing opens
 * its menu: Rename, the size (which had floated on the world until it found this home) and Delete.
 */
const clearingsRow = (t: T) => `
  <div class="clearings-row clearing-only" role="group" aria-label="${esc(t("clearing.yours"))}"></div>
  <div class="clearings-menu" role="menu" hidden>
    <p class="kicker" data-bind="clearingMenuName"></p>
    <button type="button" role="menuitem" data-action="renameClearing">${esc(t("clearing.rename"))}</button>
    <div class="clearings-size" role="group" aria-label="${esc(t("clearing.size"))}">
      <button type="button" data-action="reach:small" aria-pressed="false">${esc(t("clearing.small"))}</button>
      <button type="button" data-action="reach:large" aria-pressed="false">${esc(t("clearing.large"))}</button>
    </div>
    <button type="button" role="menuitem" data-action="deleteClearing">${esc(t("clearing.delete"))}</button>
  </div>`;

/**
 * The HUD (Daniel, 2026-10-19). The top bar chooses the world: the menu, then Today, Endless and
 * the clearing, the chosen one in ink. The bottom bar holds what belongs to the world shown: on an
 * expedition its distance, the cabin and the banner, the compass floating over the world on a
 * phone; in the clearing Arrange, Photo and the cabin, with the row of clearings under the top
 * bar, a place among places. On desktop the places stand at the top bar's start, and the distance or
 * Arrange and Photo at the bottom bar's start; the wordmark is left to the start sheet and the
 * menu (Daniel, 2026-10-19). `run-only` and `clearing-only` mark what shows in one place alone.
 * `getApp` shows Get the iOS app in the desktop top bar.
 */
export function hudHtml(t: T, getApp: boolean): string {
  return `
  <div class="hud hud-a hud-places">
    <div class="bar bar-top only-phone">
      <button type="button" class="cell icon-cell" data-action="menu" aria-label="${esc(t("hud.menu"))}">${icon("menu")}</button>
      ${places(t, "cell seg grow stack")}
    </div>
    <div class="bar bar-bottom only-phone">
      ${distanceCell(t, "kicker")}
      ${clearingModes(t, "cell seg grow stack")}
      ${cabinButton(t)}
      ${bannerButton(t, 28, "cell banner-cell run-only")}
    </div>
    ${floatingCompass(t)}
    ${clearingsRow(t)}
    <div class="bar bar-top only-desk desk-grid">
      <div class="side side-start">
        <button type="button" class="cell labelled" data-action="menu">${icon("menu")}<span>${esc(t("hud.menu"))}</span></button>
        ${places(t, "cell seg row")}
      </div>
      <div class="centre"></div>
      <div class="side side-end">
        ${getApp ? getAppButton(t) : ""}
      </div>
    </div>
    <div class="bar bar-bottom only-desk desk-grid">
      <div class="side side-start">
        ${distanceCell(t, "kicker small")}
        ${clearingModes(t, "cell seg row")}
      </div>
      <div class="centre hint-line kicker"><span data-bind="deskHint"></span></div>
      <div class="side side-end">
        ${compassButton(t)}
        ${desktopTools(t)}
        ${cabinButton(t)}
      </div>
    </div>
  </div>`;
}
